import React from "react";
import { motion } from "framer-motion";
import { Phone, Mail, Award, GraduationCap, Stethoscope } from "lucide-react";
import {
  CLINIC_PHONE_DISPLAY,
  CLINIC_PHONE_TEL,
  CLINIC_EMAIL,
} from "../lib/whatsapp";

const DOCTOR_IMAGE = "/doctor.png";

export const AboutDoctor = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-12 sm:py-16 lg:py-20 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-4"
          >
            <div className="relative max-w-xs mx-auto lg:max-w-none">
              <div className="aspect-[3/4] rounded-2xl overflow-hidden border border-slate-100 shadow-[0_20px_50px_rgba(0,0,0,0.08)]">
                <img
                  src={DOCTOR_IMAGE}
                  alt="Dr. Gaurav Kumar PT"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-3 sm:-right-5 bg-white border border-slate-100 rounded-xl px-4 py-2.5 shadow-[0_8px_24px_rgba(0,0,0,0.08)]">
                <p className="text-[10px] text-brand-600 font-semibold tracking-[0.18em] uppercase">
                  Lead Physiotherapist
                </p>
                <p className="font-display text-sm font-semibold text-slate-900 mt-0.5">
                  Dr. Gaurav Kumar PT
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-8"
          >
            <p className="text-xs sm:text-sm text-brand-600 font-semibold tracking-[0.18em] uppercase">
              About the Doctor
            </p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900">
              Personalised, evidence-based physiotherapy — at your home.
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              Dr. Gaurav Kumar PT is an experienced physiotherapist providing
              personalised home-visit treatments for pain relief, rehabilitation,
              posture correction, neurological recovery and sports injuries.
              Every plan is tailored to your body, lifestyle, and recovery
              goals.
            </p>
            <p className="mt-3 text-sm text-slate-500">
              <span className="font-medium text-slate-700">Krishna Institute of Nursing Science and Research</span>
            </p>

            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              {[
                { icon: GraduationCap, label: "Diploma in Physiotherapy" },
                { icon: Award, label: "2 Years Experience" },
                { icon: Stethoscope, label: "Home-visit Specialist" },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-3 p-4 rounded-2xl bg-slate-50 border border-slate-100"
                >
                  <span className="w-10 h-10 rounded-xl bg-white border border-slate-100 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-brand-600" strokeWidth={1.5} />
                  </span>
                  <span className="text-sm font-medium text-slate-800">
                    {label}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <a
                href={`tel:${CLINIC_PHONE_TEL}`}
                data-testid="about-call-btn"
                className="flex items-center gap-3 p-4 rounded-2xl border border-slate-200 hover:border-brand-300 hover:bg-brand-50/40 transition-colors group"
              >
                <span className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-brand-600" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-xs text-slate-500">Call</p>
                  <p className="text-sm font-semibold text-slate-900 group-hover:text-brand-700">
                    {CLINIC_PHONE_DISPLAY}
                  </p>
                </div>
              </a>
              <a
                href={`mailto:${CLINIC_EMAIL}`}
                data-testid="about-email-btn"
                className="flex items-center gap-3 p-4 rounded-2xl border border-slate-200 hover:border-brand-300 hover:bg-brand-50/40 transition-colors group"
              >
                <span className="w-10 h-10 rounded-xl bg-accent-50 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-accent-600" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-xs text-slate-500">Email</p>
                  <p className="text-sm font-semibold text-slate-900 break-all group-hover:text-accent-700">
                    {CLINIC_EMAIL}
                  </p>
                </div>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
